/**
 * Process map (ADR-0056): orchestrator `GET /v1/insights/agents/{tenant}/{agent}/flow` and
 * `…/flow/requests`. Contracts follow platform/services/orchestrator (insights.py).
 */
import type { Http } from "./http";
import type { Text } from "./types";

const enc = encodeURIComponent;

export const FLOW_START = "__start__";
export type FlowOutcome = "completed" | "refused" | "failed" | "cancelled" | "waiting" | "running";
export const FLOW_OUTCOMES: readonly FlowOutcome[] = ["completed", "refused", "failed", "cancelled", "waiting", "running"];
/** Outcome node id per outcome (`__completed__` …). */
export const OUTCOME_NODE: Record<FlowOutcome, string> = {
  completed: "__completed__",
  refused: "__refused__",
  failed: "__failed__",
  cancelled: "__cancelled__",
  waiting: "__waiting__",
  running: "__running__",
};

export type FlowNodeKind = "start" | "step" | FlowOutcome;

export interface FlowStats {
  count: number;
  median_ms: number;
  p90_ms: number;
  max_ms: number;
}

export interface FlowMapNode {
  id: string;
  kind: FlowNodeKind;
  /** Graph node type from the spec (llm, tool, human_approval, …); null for outcome nodes. */
  type: string | null;
  label: Text | null;
  /** Distinct requests that reached the node. */
  requests: number;
  visits: number;
  work: FlowStats | null;
  /** Time waiting for a person at this step. */
  wait: FlowStats | null;
  waited_requests: number;
  outcomes: Partial<Record<FlowOutcome, number>>;
}

export interface FlowMapEdge {
  source: string;
  target: string;
  requests: number;
  transitions: number;
  outcomes: Partial<Record<FlowOutcome, number>>;
}

export type RefusalKind = "guardrail" | "guardrail_input" | "guardrail_output" | "policy" | "approval_rejected" | string;

export interface ReasonCount {
  kind: RefusalKind | "failure";
  reason: string;
  node: string | null;
  count: number;
}

export interface Flow {
  tenant: string;
  agent: string;
  version: string | null;
  spec_version: string | null;
  from: string;
  to: string;
  requests: number;
  total_requests: number;
  truncated: boolean;
  outcomes: Partial<Record<FlowOutcome, number>>;
  versions: { version: string; requests: number }[];
  nodes: FlowMapNode[];
  edges: FlowMapEdge[];
  refusals: ReasonCount[];
  failures: ReasonCount[];
}

export interface FlowRequest {
  run_id: string;
  agent_ref: string;
  version: string;
  outcome: FlowOutcome;
  status: string;
  user_id: string | null;
  /** Start of the request text; null for roles that do not read run content. */
  excerpt: string | null;
  started_at: string;
  ended_at: string | null;
  duration_ms: number | null;
  work_ms: number | null;
  wait_ms: number | null;
  refusal: ReasonCount | null;
  failure: string | null;
  waiting_at: string | null;
}

export interface FlowRequestPage {
  items: FlowRequest[];
  total: number;
  offset: number;
  limit: number;
}

export interface FlowQuery {
  version?: string;
  /** ISO 8601 with offset. */
  from?: string;
  to?: string;
}

export interface FlowRequestQuery extends FlowQuery {
  node?: string;
  /** `source>target`. */
  edge?: string;
  outcome?: FlowOutcome;
  offset?: number;
  limit?: number;
}

/** A requester's display name for the process map (from identity `GET /v1/directory`, ADR-0054). */
export interface InsightsPerson {
  id: string;
  display_name: string;
}

/** The directory answers `{id: {display_name, kind}}` for ids of the caller's tenant (ADR-0054). */
function toPeople(body: unknown): InsightsPerson[] {
  if (!body || typeof body !== "object" || Array.isArray(body)) return [];
  const out: InsightsPerson[] = [];
  for (const [id, raw] of Object.entries(body as Record<string, unknown>)) {
    const name = raw && typeof raw === "object" ? (raw as { display_name?: unknown }).display_name : undefined;
    if (typeof name === "string" && name.trim() !== "") out.push({ id, display_name: name });
  }
  return out;
}

export function insightsApi(http: Http) {
  return {
    flow: (tenant: string, agent: string, q: FlowQuery = {}) =>
      http.get<Flow>("orchestrator", `/v1/insights/agents/${enc(tenant)}/${enc(agent)}/flow`, { ...q }),
    requests: (tenant: string, agent: string, q: FlowRequestQuery = {}) =>
      http.get<FlowRequestPage>("orchestrator", `/v1/insights/agents/${enc(tenant)}/${enc(agent)}/flow/requests`, { ...q }),
    /** Display names for requester ids; ids the directory doesn't resolve are left out. */
    directory: async (ids: string[]) => toPeople(await http.get<unknown>("identity", "/v1/directory", { ids: ids.join(",") })),
  };
}

export type InsightsApi = ReturnType<typeof insightsApi>;
